<template>
  <div class="travel-album">
    <!-- 前台顶栏 -->
    <header-nav></header-nav>

    <!-- Leaflet 地图（earthol 形态：多瓦片源卫星/地形图，承载旅行足迹） -->
    <div id="earth-map" class="map"></div>

    <!-- 地图右上角：足迹总里程角标 -->
    <div class="mileage-badge" v-if="kmLoading || totalKm > 0">
      <div v-if="kmLoading" class="mileage-num">🚗 里程计算中…</div>
      <div v-else class="mileage-num">🚗 {{ totalKm }} <span>km</span></div>
      <div class="mileage-sub">驾车足迹总里程 · {{ cityCount }} 个城市</div>
    </div>

    <!-- 左侧：旅行相册面板 -->
    <div class="album-panel">
      <div class="panel-header">
        <h2>🌸 地图旅游相册</h2>
        <p class="stats">已打卡 <b>{{ memories.length }}</b> 个地方 · 足迹 <b>{{ cityCount }}</b> 个城市 · 总里程 <b>{{ totalKm }}</b> km</p>
      </div>
      <div class="memory-list">
        <div
          v-for="(m, idx) in sortedMemories"
          :key="m.id"
          class="memory-card"
          @click="viewMemory(m)"
        >
          <video v-if="isVideo(firstPhoto(m))" class="thumb thumb-video" :src="imgUrl(firstPhoto(m))" preload="metadata" muted playsinline></video>
          <img v-else class="thumb" :src="imgUrl(firstPhoto(m)) || defaultPhoto(m)" alt="" />
          <div class="card-info">
            <div class="card-title">
              <span class="card-no">#{{ idx + 1 }}</span>
              <span class="card-title-text">{{ m.title }}</span>
            </div>
            <div class="card-date">{{ m.date }} · {{ m.name }}</div>
          </div>
        </div>
        <div v-if="!memories.length" class="empty">
          还没有旅行记忆<br />点击地图任意位置即可添加
        </div>
      </div>
      <div class="panel-footer">
        <el-button type="primary" round plain @click="openAddDialog">＋ 添加旅行记忆</el-button>
      </div>
    </div>

    <!-- 右上：搜索与工具条 -->
    <div class="top-bar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          class="search-input"
          type="text"
          placeholder="搜索地点…"
          @keyup.enter="searchLocation"
        />
        <el-button size="mini" type="primary" plain @click="searchLocation" style="border-radius: 12px">搜索</el-button>
      </div>
      <el-button
        size="mini"
        round
        :type="showRoute ? 'primary' : 'default'"
        @click="toggleRoute"
      >足迹路线</el-button>
      <el-button size="mini" round @click="resetView">全国总览</el-button>
      <el-button size="mini" round @click="clearAll">清空相册</el-button>
      <el-button size="mini" round @click="toggleFullscreen">⛶ 全屏</el-button>
      <el-button size="mini" round @click="shareLocation">🔗 分享</el-button>
    </div>

    <!-- 经纬度定位（earthol 形态） -->
    <div class="coord-bar">
      <span class="coord-label">经纬度</span>
      <el-input v-model="lngInput" class="coord-input" size="mini" placeholder="经度" />
      <el-input v-model="latInput" class="coord-input" size="mini" placeholder="纬度" />
      <el-button size="mini" type="primary" plain @click="goToCoord">前往</el-button>
      <span class="coord-current" v-if="centerCoord">{{ centerCoord }}</span>
    </div>

    <!-- 地图数据源切换（earthol 形态） -->
    <div class="source-switch">
<!--      <span class="source-label">地图数据源</span>-->
      <el-radio-group v-model="mapSource" size="mini" @change="changeMapSource">
        <el-radio-button label="amapSatellite">高德卫星</el-radio-button>
        <el-radio-button label="esriSatellite">全球影像</el-radio-button>
        <el-radio-button label="amapVec">标准地图</el-radio-button>
        <el-radio-button label="esriTerrain">地形晕渲</el-radio-button>
        <el-radio-button label="esriTopo">等高地形</el-radio-button>
      </el-radio-group>
    </div>

    <!-- 底部操作提示 👆 点击地图任意位置，记录一段旅行记忆-->
<!--    <div class="hint">-->
<!--      <el-radio-group v-model="mapSource" size="mini" @change="changeMapSource">-->
<!--      <el-radio-button label="amapSatellite">高德卫星</el-radio-button>-->
<!--      <el-radio-button label="esriSatellite">全球影像</el-radio-button>-->
<!--      <el-radio-button label="amapVec">标准地图</el-radio-button>-->
<!--      <el-radio-button label="esriTerrain">地形晕渲</el-radio-button>-->
<!--      <el-radio-button label="esriTopo">等高地形</el-radio-button>-->
<!--    </el-radio-group>-->
<!--    </div>-->

    <!-- 添加旅行记忆对话框 -->
    <el-dialog
      :title="editingId ? '✏️ 编辑旅行记忆' : '🌸 添加旅行记忆'"
      :visible.sync="showAdd"
      width="520px"
      custom-class="album-dialog"
      :close-on-click-modal="false"
      @close="closeAdd"
    >
      <div class="form">
        <div class="field">
          <label>地点</label>
          <el-input v-model="form.name" placeholder="点击地图定位，也可手动修改" />
        </div>
        <div class="field">
          <label>标题</label>
          <el-input v-model="form.title" placeholder="比如：西湖边的洛丽塔下午" />
        </div>
        <div class="field">
          <label>排序编号</label>
          <el-input-number
            v-model="form.sortNo"
            :min="1"
            :controls="false"
            placeholder="留空自动编号"
            style="width: 100%"
          />
          <div class="field-tip">留空则自动排在最后；填写编号可插入到指定位置</div>
        </div>
        <div class="field">
          <label>日期</label>
          <el-date-picker
            v-model="form.date"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="选择日期"
            style="width: 100%"
          />
        </div>
        <div class="field">
          <label>描述</label>
          <el-input
            v-model="form.description"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="记录此刻的心情…"
          />
        </div>
        <div class="field">
          <label>照片/视频（可上传多张，同地点图片与视频会在详情中轮播）</label>
          <div class="photo-row">
            <div class="photo-thumbs">
              <div v-for="(p, i) in formPhotoList" :key="p + '_' + i" class="photo-thumb-wrap">
                <video v-if="isVideo(p)" class="photo-thumb video-thumb" :src="imgUrl(p)" preload="metadata" muted playsinline></video>
                <img v-else class="photo-thumb" :src="imgUrl(p)" alt="" />
                <span class="photo-thumb-del" title="移除这张" @click="removeFormPhoto(i)">×</span>
              </div>
            </div>
            <el-button size="small" @click="$refs.fileInput.click()">
              {{ formPhotoList.length ? '继续上传' : '上传照片/视频' }}
            </el-button>
            <input
              ref="fileInput"
              type="file"
              accept="image/*,video/*"
              multiple
              style="display: none"
              @change="onFilesChange"
            />
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="closeAdd">取消</el-button>
        <el-button type="primary" @click="saveMemory">{{ editingId ? '保存修改' : '保存' }}</el-button>
      </span>
    </el-dialog>

    <!-- 旅行记忆详情：图片居中放大、信息固定在底部、左右切换上/下一站 -->
    <el-dialog
      :visible.sync="showDetail"
      custom-class="album-dialog detail-dialog"
      :close-on-click-modal="true"

      width="76vw"

      top="8vh"
    >
      <div v-if="current" class="detail-stage">
        <!-- 中间：放大图片 -->
        <div class="detail-stage-body">
          <video v-if="isCurPhotoVideo" class="detail-photo-big video-big" :src="curPhotoUrl" controls preload="metadata"></video>
          <img v-else class="detail-photo-big" :src="curPhotoUrl" alt="" />
          <button class="nav-arrow nav-prev" :disabled="!hasPrevPhoto" @click="prevPhoto" title="上一张">‹</button>
          <button class="nav-arrow nav-next" :disabled="!hasNextPhoto" @click="nextPhoto" title="下一张">›</button>
          <div class="detail-count">{{ currentIndex + 1 }} / {{ memories.length }}<template v-if="curPhotoCount > 1"> · 图 {{ curPhotoIdx + 1 }}/{{ curPhotoCount }}</template></div>
        </div>
        <!-- 底部：信息 + 操作按钮 -->
        <div class="detail-info">
          <h3>{{ current.title }}</h3>
          <p class="detail-meta">📍 {{ current.name }} · 📅 {{ current.date }}</p>
          <p v-if="curKmLoading || curPrevKm !== null || curNextKm !== null" class="detail-dist">
            <template v-if="curPrevKm !== null">🚗 距上一站 {{ curPrevKm }} km（驾车）</template>
            <template v-if="curPrevKm !== null && curNextKm !== null"> · </template>
            <template v-if="curNextKm !== null">🚗 距下一站 {{ curNextKm }} km（驾车）</template>
            <template v-if="curKmLoading">（计算中…）</template>
          </p>
          <p class="detail-desc">{{ current.description || '【您没有留下描述 ！】' }}</p>
          <div class="detail-actions">
            <el-button size="small" round class="loc-arrow" :disabled="!hasPrev" @click="prevMemory" title="上一地点">‹ 上一地点</el-button>
            <el-button size="small" plain @click="moveMemory('up')">↑ 上移</el-button>
            <el-button size="small" plain @click="moveMemory('down')">↓ 下移</el-button>
            <el-button size="small" type="warning" plain @click="openEdit">✏️ 编辑</el-button>
            <el-button size="small" type="danger" plain @click="deleteMemory">删除记忆</el-button>
            <el-button size="small" type="primary" @click="goToCurrent">地图定位</el-button>
            <el-button size="small" round class="loc-arrow" :disabled="!hasNext" @click="nextMemory" title="下一地点">下一地点 ›</el-button>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
/**
 * 地图旅游相册（earthol 形态）
 * - Leaflet 多瓦片源展示旅行足迹：高清卫星（高德/Esri）/ 标准图 / 地形图可一键切换
 * - 粉色打卡标记 + 足迹路线连线（按日期顺序）
 * - 点击地图任意位置 / 搜索地点，即可添加一条旅行记忆（标题、日期、描述、照片）
 * - 左侧相册卡片列表：点击卡片在地图上定位并查看详情
 * - 地点数据存数据库 map_album 表并绑定账号；照片上传到 map-img 目录
 * - 坐标体系：数据库存 BD09；地图渲染用 Leaflet（GCJ02/WGS84 瓦片），双向转换复用下方算法
 * - 地理编码/逆地理编码使用 Photon（OpenStreetMap 免费服务，免 key），坐标 WGS84 ↔ BD09 互转
 */
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import lolitaImg from '../TimeAlbum/assets/lolita.jpg'
import hanfuImg from '../TimeAlbum/assets/hanfu.jpg'
import jkImg from '../TimeAlbum/assets/jk.jpg'
import petalImg from '../TimeAlbum/assets/petal.jpg'
import treeImg from '../TimeAlbum/assets/tree.jpg'
import skyImg from '../TimeAlbum/assets/sky.jpg'
import HeaderNav from '@/components/HeaderNav.vue'
import bus from '@/utils/bus'
import TileCache from './tile-cache'

// 未上传照片时按 id 轮换使用的默认配图（主题：少女旅行照）
const DEFAULT_PHOTOS = [lolitaImg, hanfuImg, jkImg, petalImg, treeImg, skyImg]

// ---------------- earthol 形态：多瓦片数据源 ----------------
// 坐标体系设计（关键）：
// 高德瓦片网格基于 GCJ02 坐标系，Esri/OSM 等国际源基于 WGS84。
// Leaflet 单个地图只能有一个坐标系，这里把主坐标系设为 GCJ02（投影数学同 Web Mercator）：
// - 高德源（默认）瓦片按 GCJ02 网格直接请求 → 与标记同基准，零误差对齐
// - WGS84 源瓦片通过 WgsTileLayer 做瓦片级 + 亚像素级纠偏（按瓦片中心换算
//   WGS84 瓦片号，再按像素差平移内容），与标记/路线像素级重合
// - 库内坐标仍为 BD09；标记/路线显示用 bd2gcj；点击取点用 gcj2wgs 还原真实 WGS84 再转 BD09 存库
const TILE_SOURCES = {
  // 高德卫星影像（国内高清、访问快；卫星域名 webst）
  amapSat: {
    url: 'https://webst0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=6&x={x}&y={y}&z={z}',
    options: { subdomains: ['1', '2', '3', '4'], maxZoom: 18, noWrap: true, attribution: '© 高德地图' }
  },
  // 高德地名/路网标注层（叠加在高德卫星底图上）
  amapLabel: {
    url: 'https://webst0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=8&x={x}&y={y}&z={z}',
    options: { subdomains: ['1', '2', '3', '4'], maxZoom: 18, noWrap: true, attribution: '© 高德地图' }
  },
  // 高德矢量标准图（国内街道图，替代国外 OSM 保证可加载）
  amapVec: {
    url: 'https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=7&x={x}&y={y}&z={z}',
    options: { subdomains: ['1', '2', '3', '4'], maxZoom: 18, noWrap: true, attribution: '© 高德地图' }
  },
  // Esri 全球高清卫星影像（WGS84 系瓦片，需纠偏到 GCJ02 网格）
  esriSat: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    options: { maxZoom: 19, attribution: '© Esri, Maxar, Earthstar Geographics' },
    wgs: true
  },
  // Esri 全球地形晕渲图（earthol「全球地形图」同款观感，WGS84 系）
  esriRelief: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Shaded_Relief/MapServer/tile/{z}/{y}/{x}',
    options: { maxZoom: 16, attribution: '© Esri' },
    wgs: true
  },
  // Esri 全球等高线地形图（含等高线/地形注记，替代国外 OpenTopoMap，WGS84 系）
  esriTopo: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
    options: { maxZoom: 17, attribution: '© Esri' },
    wgs: true
  }
}

// 数据源切换项：layers 为底图，overlay 为叠加标注层
const MAP_SOURCES = [
  { key: 'amapSatellite', label: '高德卫星', layers: ['amapSat'], overlay: ['amapLabel'] },
  { key: 'esriSatellite', label: '全球影像', layers: ['esriSat'] },
  { key: 'amapVec', label: '标准地图', layers: ['amapVec'] },
  { key: 'esriTerrain', label: '地形晕渲', layers: ['esriRelief'] },
  { key: 'esriTopo', label: '等高线地形', layers: ['esriTopo'] }
]

// 主坐标系：GCJ02（投影数学与 Web Mercator 相同，仅经纬度语义为 GCJ02）
// 高德瓦片按此网格请求即零误差；覆盖物坐标也统一用 GCJ02 数值显示
const GCJCRS = L.Util.extend({}, L.CRS.EPSG3857)

// WGS84 系瓦片亚像素纠偏图层：主坐标系为 GCJ02（瓦片网格即 GCJ 网格）。
// 只按瓦片号换算（整张瓦片取整）会残留数百米偏移——GCJ02 与 WGS84 的偏差
// 通常在 0~600m（z13 约 26px），远小于一张瓦片（256px），取整后等于没纠偏。
// 这里改为两层校正：
// 1) 由 GCJ 槽中心反算真实 WGS84，求出应请求的 WGS84 瓦片号（瓦片级）；
// 2) 求出该真实位置与槽中心的像素差，用 CSS transform 对瓦片内容做亚像素平移，
//    使 Esri/OSM 等 WGS 源瓦片与标记/路线像素级重合（残差 = 瓦片内畸变变化，<1px）。
const WgsTileLayer = L.TileLayer.extend({
  // WGS 瓦片 URL：瓦片号由 GCJ 槽中心换算得出
  getTileUrl(coords) {
    const w = this._tilePlan(coords)
    return this._url
      .replace('{s}', this._getSubdomain(coords))
      .replace('{x}', w.x)
      .replace('{y}', w.y)
      .replace('{z}', coords.z)
  },
  // 瓦片规划：GCJ 槽中心像素 → GCJ02 经纬度 → 真实 WGS84 → Web Mercator 像素
  // → WGS84 瓦片号（x/y）+ 亚像素平移量（ox/oy）
  _tilePlan(coords) {
    const z = coords.z
    const size = this.getTileSize()
    const crs = this._map.options.crs
    const slotCx = (coords.x + 0.5) * size.x
    const slotCy = (coords.y + 0.5) * size.y
    // GCJ 槽中心对应的 GCJ02 经纬度
    const gcj = crs.pointToLatLng(L.point(slotCx, slotCy), z)  // 像素空间 API（crs.unproject 是裸投影，会把像素当米解析）
    // 同一真实地点的 WGS84 坐标
    const wgs = this.options.transform(gcj.lng, gcj.lat)
    // 其在 Web Mercator 像素空间中的位置（与地图同一像素空间）
    const p = crs.latLngToPoint(L.latLng(wgs.lat, wgs.lng), z)  // 像素空间 API（SphericalMercator.project 返回米，不缩放）
    // 应请求的 WGS84 瓦片号
    const wx = Math.floor(p.x / size.x)
    const wy = Math.floor(p.y / size.y)
    // 亚像素偏移：让 WGS 瓦片中心内容精确落在 GCJ 槽中心
    const ox = (wx + 0.5) * size.x - p.x
    const oy = (wy + 0.5) * size.y - p.y
    return { x: wx, y: wy, ox: ox, oy: oy }
  },
  // 瓦片 DOM：外层容器由 Leaflet 定位到 GCJ 槽位，内层 img 按亚像素偏移平移
  createTile(coords, done) {
    const plan = this._tilePlan(coords)
    const size = this.getTileSize()
    const wrap = L.DomUtil.create('div', 'wgs-tile-wrap')
    wrap.style.width = size.x + 'px'
    wrap.style.height = size.y + 'px'
    const img = document.createElement('img')
    img.style.width = size.x + 'px'
    img.style.height = size.y + 'px'
    img.style.transform = 'translate(' + plan.ox + 'px,' + plan.oy + 'px)'
    img.alt = ''
    // 复用 TileLayer 自带的加载/错误处理：done(null, img) 交由 _tileReady
    // 完成淡入与置可见（img 传参供其 getAttribute('src') 守卫使用）
    L.DomEvent.on(img, 'load', L.Util.bind(this._tileOnLoad, this, done, img))
    L.DomEvent.on(img, 'error', L.Util.bind(this._tileOnError, this, done, img))
    img.src = this.getTileUrl(coords)
    wrap.appendChild(img)
    return wrap
  }
})

// ---------------- 本地瓦片缓存（IndexedDB + LRU） ----------------
// 在 Leaflet 默认 createTile 基础上接入缓存：
// 命中 → 直接使用 object URL（秒开）；未命中 → 原 URL 网络加载，加载完成后回存。
// tileunload 时标记已移除并回收 object URL，避免路由切换泄漏。
function cacheFillTile(tile, coords) {
  const layer = this
  const url = layer.getTileUrl(coords)
  tile._tileCacheUrl = url
  TileCache.getObjectUrl(url).then(objUrl => {
    if (tile._tileAborted || !layer._map) return
    if (objUrl) {
      tile.src = objUrl
    } else {
      tile.src = url
      tile.addEventListener('load', () => TileCache.saveFromNetwork(url), { once: true })
    }
  })
}

// 普通瓦片层（高德等非 WGS 源）接入缓存
const CachedTileLayer = L.TileLayer.extend({
  createTile(coords, done) {
    const tile = document.createElement('img')
    L.DomEvent.on(tile, 'load', L.Util.bind(this._tileOnLoad, this, done, tile))
    L.DomEvent.on(tile, 'error', L.Util.bind(this._tileOnError, this, done, tile))
    tile.alt = ''
    cacheFillTile.call(this, tile, coords)
    return tile
  }
})

// WGS 纠偏瓦片层（Esri 源）接入缓存：保留亚像素纠偏，替换 src 为缓存/网络路径
const CachedWgsTileLayer = WgsTileLayer.extend({
  createTile(coords, done) {
    const plan = this._tilePlan(coords)
    const size = this.getTileSize()
    const wrap = L.DomUtil.create('div', 'wgs-tile-wrap')
    wrap.style.width = size.x + 'px'
    wrap.style.height = size.y + 'px'
    const img = document.createElement('img')
    img.style.width = size.x + 'px'
    img.style.height = size.y + 'px'
    img.style.transform = 'translate(' + plan.ox + 'px,' + plan.oy + 'px)'
    img.alt = ''
    L.DomEvent.on(img, 'load', L.Util.bind(this._tileOnLoad, this, done, img))
    L.DomEvent.on(img, 'error', L.Util.bind(this._tileOnError, this, done, img))
    cacheFillTile.call(this, img, coords)
    wrap.appendChild(img)
    return wrap
  }
})



export default {
  name: "MapTracking",
  components: { HeaderNav },
  data() {
    return {
      map: null,
      memories: [], // 旅行记忆数组
      markerMap: {}, // memoryId -> { marker }
      routeLine: null, // 足迹路线折线
      showRoute: true, // 是否显示足迹路线
      searchQuery: '',
      // earthol 形态：数据源切换 / 经纬度定位 / 全屏 / 分享
      mapSource: 'amapSatellite', // 当前地图数据源 key（默认高德卫星）
      tileLayers: [], // 当前数据源的瓦片图层（可含底图 + 标注叠加）
      lngInput: '', // 经纬度定位：经度输入（真实 WGS84）
      latInput: '', // 经纬度定位：纬度输入
      centerCoord: '', // 地图中心点坐标实时显示（WGS84）
      showAdd: false,
      showDetail: false,
      curPhotoIdx: 0, // 详情弹窗：当前地点正在查看的第几张图（多图轮播）
      clickPoint: null, // 点击地图定位的待添加位置（BD09）
      clickName: '', // 点击地图定位得到的地点名
      clickNotify: null, // 点击地图定位后的右下角询问通知
      tempMarker: null, // 点击地图定位的临时标记（询问是否添加）
      current: null, // 当前查看的记忆
      form: {
        name: '',
        title: '',
        date: '',
        description: '',
        photo: '',
        sortNo: null // 编号：留空自动补
      },
      pendingPoint: null, // 点击地图得到的待添加位置
      searchPoint: null, // 搜索定位的临时坐标（点击通知「添加」后才生效）
      searchName: '', // 搜索定位的地点名
      searchNotify: null, // 搜索通知实例（点击添加后主动关闭）
      editingId: null, // 正在编辑的记忆 id（null 表示新增模式）
      totalKm: 0, // 总里程（真实驾车路线距离，异步计算）
      kmLoading: false, // 总里程计算中
      curPrevKm: null, // 详情弹窗：与上一站的驾车距离（km）
      curNextKm: null, // 详情弹窗：与下一站的驾车距离（km）
      curKmLoading: false, // 详情弹窗相邻距离计算中
      user: null // 当前登录用户（sessionStorage CurUser）
    }
  },
  computed: {
    // 相册列表按用户自定义顺序（sortNo 升序，可上移/下移调整）
    sortedMemories() {
      return [...this.memories].sort((a, b) => (a.sortNo - b.sortNo) || (a.id - b.id))
    },

    // 足迹覆盖城市数（按地点名去重）
    cityCount() {
      const names = new Set()
      this.memories.forEach(m => {
        if (m.name) names.add(m.name)
      })
      return names.size
    },

    // 表单中的照片列表（photo 字段为「|」分隔的多图路径串）
    formPhotoList() {
      return this.photoList(this.form.photo)
    },

    // 当前详情地点的全部图片路径
    curPhotos() {
      return this.current ? this.photoList(this.current.photo) : []
    },

    // 详情弹窗当前展示的图片地址（多图轮播，单图保持原逻辑）
    curPhotoUrl() {
      if (!this.current) return ''
      const list = this.curPhotos
      const p = list[this.curPhotoIdx]
      return p ? this.imgUrl(p) : (this.imgUrl(this.current.photo) || this.defaultPhoto(this.current))
    },
    // 当前展示的媒体是否为视频（视频在详情中手动点击播放，不自动播放）
    isCurPhotoVideo() {
      if (!this.current) return false
      const list = this.curPhotos
      return list.length ? this.isVideo(list[this.curPhotoIdx] || list[0]) : false
    },
    curPhotoCount() {
      return this.curPhotos.length
    },
    hasPrevPhoto() {
      return this.curPhotoIdx > 0
    },
    hasNextPhoto() {
      return this.curPhotoIdx < this.curPhotoCount - 1
    },

    // 详情弹窗：当前查看的记忆在排序列表中的下标
    currentIndex() {
      if (!this.current || !this.memories.length) return -1
      return this.memories.findIndex(m => m.id === this.current.id)
    },
    hasPrev() {
      return this.currentIndex > 0
    },
    hasNext() {
      return this.currentIndex > -1 && this.currentIndex < this.memories.length - 1
    }
  },
  created() {
    // 读取当前登录账号（sessionStorage CurUser，与项目其他组件一致）
    this.readLoginUser()
    if (!this.user || !this.user.no) {
      this.$message({ message: '请先登录，旅行相册会绑定你的账号', type: 'warning', duration: 3000 })
    }
    this.loadMemories()
    // 监听登录/登出广播（HeaderNav 统一发出），登录后自动加载该账号的记忆，无需手动刷新
    bus.$on('music:user-changed', this.onUserChanged)
  },
  mounted() {
    this.$nextTick(() => {
      this.initMap()
    })
    // 详情弹窗：键盘 ←/→ 切换上/下一站
    document.addEventListener('keydown', this.handleDetailKey)
  },
  beforeDestroy() {
    // 组件销毁时清理地图图层并解绑登录监听
    bus.$off('music:user-changed', this.onUserChanged)
    document.removeEventListener('keydown', this.handleDetailKey)
    if (this.map) {
      if (this.routeLine) this.map.removeLayer(this.routeLine)
      Object.keys(this.markerMap).forEach(id => this.removeMarker(id))
      this.map.remove()
      this.map = null
      // 回收本次会话登记的瓦片 object URL（组件销毁时）
      TileCache.revokeAll()
    }
  },
  methods: {
    // ---------------- 距离与里程 ----------------
    // 按用户自定义顺序排列的足迹（sortNo 升序），作为里程/相邻距离/足迹路线的计算基准
    ordered() {
      return [...this.memories].sort((a, b) => (a.sortNo - b.sortNo) || (a.id - b.id))
    },

    // 两地点间直线距离（Haversine 公式，单位 km），作为驾车路线不可用时的兜底
    distanceKm(a, b) {
      if (!a || !b) return 0
      const R = 6371
      const rad = d => d * Math.PI / 180
      const dLat = rad(b.lat - a.lat)
      const dLng = rad(b.lng - a.lng)
      const h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
      return 2 * R * Math.asin(Math.sqrt(h))
    },

    // ---------------- 坐标转换（WGS84 / GCJ02 / BD09） ----------------
    // 百度地图使用 BD09 坐标系；OSRM 返回 WGS84，绘制前需转换
    // 以下为标准公开偏移算法（gcj02 国测局加密坐标）
    _coordPi() {
      return 3.1415926535897932384626 * 3000.0 / 180.0
    },
    _outOfChina(lat, lng) {
      return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271
    },
    _txLat(lng, lat) {
      const PI = Math.PI
      let ret = -100.0 + 2.0 * lng + 3.0 * lat + 0.2 * lat * lat + 0.1 * lng * lat + 0.2 * Math.sqrt(Math.abs(lng))
      ret += (20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0 / 3.0
      ret += (20.0 * Math.sin(lat * PI) + 40.0 * Math.sin(lat / 3.0 * PI)) * 2.0 / 3.0
      ret += (160.0 * Math.sin(lat / 12.0 * PI) + 320 * Math.sin(lat * PI / 30.0)) * 2.0 / 3.0
      return ret
    },
    _txLng(lng, lat) {
      const PI = Math.PI
      let ret = 300.0 + lng + 2.0 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * Math.sqrt(Math.abs(lng))
      ret += (20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0 / 3.0
      ret += (20.0 * Math.sin(lng * PI) + 40.0 * Math.sin(lng / 3.0 * PI)) * 2.0 / 3.0
      ret += (150.0 * Math.sin(lng / 12.0 * PI) + 300.0 * Math.sin(lng / 30.0 * PI)) * 2.0 / 3.0
      return ret
    },
    // WGS84 -> GCJ02
    wgs2gcj(lng, lat) {
      if (this._outOfChina(lat, lng)) return { lng, lat }
      const PI = Math.PI
      let dLat = this._txLat(lng - 105.0, lat - 35.0)
      let dLng = this._txLng(lng - 105.0, lat - 35.0)
      const radLat = lat / 180.0 * PI
      let magic = Math.sin(radLat)
      magic = 1 - 0.00669342162296594323 * magic * magic
      const sqrtMagic = Math.sqrt(magic)
      dLat = (dLat * 180.0) / ((6378245.0 * (1 - 0.00669342162296594323)) / (magic * sqrtMagic) * PI)
      dLng = (dLng * 180.0) / (6378245.0 / sqrtMagic * Math.cos(radLat) * PI)
      return { lng: lng + dLng, lat: lat + dLat }
    },
    // GCJ02 -> BD09
    gcj2bd(lng, lat) {
      const xPi = this._coordPi()
      const z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(lat * xPi)
      const theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(lng * xPi)
      return { lng: z * Math.cos(theta) + 0.0065, lat: z * Math.sin(theta) + 0.006 }
    },
    // BD09 -> GCJ02
    bd2gcj(lng, lat) {
      const xPi = this._coordPi()
      const x = lng - 0.0065
      const y = lat - 0.006
      const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * xPi)
      const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * xPi)
      return { lng: z * Math.cos(theta), lat: z * Math.sin(theta) }
    },
    // GCJ02 -> WGS84（迭代反偏移）
    gcj2wgs(lng, lat) {
      if (this._outOfChina(lat, lng)) return { lng, lat }
      const g = this.wgs2gcj(lng, lat)
      let tLng = lng - (g.lng - lng)
      let tLat = lat - (g.lat - lat)
      for (let i = 0; i < 3; i++) {
        const tg = this.wgs2gcj(tLng, tLat)
        tLng = lng - (tg.lng - tLng)
        tLat = lat - (tg.lat - tLat)
      }
      return { lng: tLng, lat: tLat }
    },
    // BD09 -> WGS84（请求 OSRM 前使用）
    bd2wgs(lng, lat) {
      const g = this.bd2gcj(lng, lat)
      return this.gcj2wgs(g.lng, g.lat)
    },
    // WGS84 -> BD09（绘制到百度地图前使用）
    wgs2bd(lng, lat) {
      const g = this.wgs2gcj(lng, lat)
      return this.gcj2bd(g.lng, g.lat)
    },
    // BD09（库内坐标）→ 主坐标系 GCJ02 的显示坐标（地图 CRS 为 GCJ02，标记/路线统一按 GCJ02 显示）
    displayCoord(lng, lat) {
      return this.bd2gcj(lng, lat)
    },

    // ---------------- 真实驾车路线（OSRM 开源全球道路数据，无 key、支持浏览器跨域） ----------------
    // 百度路线规划服务对该 ak 已禁用（status 240），改用 OSRM；
    // 返回 { km, path }：km 为真实驾车距离（失败降级直线距离），path 为沿道路途经点（BD09，失败为 null）
    // 结果按坐标对缓存，重复计算直接命中；20 秒超时兜底避免境外服务卡住
    routePlan(a, b) {
      const key = Math.round(a.lat * 1000) + ',' + Math.round(a.lng * 1000) + '|' +
        Math.round(b.lat * 1000) + ',' + Math.round(b.lng * 1000)
      if (!this._kmCache) this._kmCache = {}
      if (this._kmCache[key]) {
        return Promise.resolve(this._kmCache[key])
      }
      const fallback = () => ({ km: Math.round(this.distanceKm(a, b) * 10) / 10, path: null })
      return new Promise(resolve => {
        // 标记点为 BD09，先转 WGS84 再请求 OSRM，返回后再转回 BD09 绘制
        const wa = this.bd2wgs(a.lng, a.lat)
        const wb = this.bd2wgs(b.lng, b.lat)
        const url = 'https://router.project-osrm.org/route/v1/driving/' +
          wa.lng + ',' + wa.lat + ';' + wb.lng + ',' + wb.lat +
          '?overview=full&geometries=geojson'
        let settled = false
        const finish = result => {
          if (settled) return
          settled = true
          this._kmCache[key] = result
          resolve(result)
        }
        const timer = setTimeout(() => finish(fallback()), 20000)
        fetch(url)
          .then(r => r.json())
          .then(data => {
            clearTimeout(timer)
            if (data && data.code === 'Ok' && data.routes && data.routes.length > 0) {
              const route = data.routes[0]
              const pts = (route.geometry.coordinates || []).map(c => {
                const b = this.wgs2bd(c[0], c[1])
                return { lng: b.lng, lat: b.lat } // BD09 普通坐标对象
              })
              finish({ km: Math.round(route.distance / 1000 * 10) / 10, path: pts })
            } else {
              finish(fallback())
            }
          })
          .catch(() => {
            clearTimeout(timer)
            finish(fallback())
          })
      })
    },

    // 两点间驾车距离（km）：供总里程/相邻距离计算使用
    routeKm(a, b) {
      return this.routePlan(a, b).then(r => r.km)
    },

    // 总里程：按用户顺序相邻点驾车路线距离累加（异步，串行计算）
    async calcTotalKm() {
      if (!this.map) return
      this.kmLoading = true
      const runId = (this._kmRun = (this._kmRun || 0) + 1)
      const list = this.ordered()
      let sum = 0
      for (let i = 1; i < list.length; i++) {
        sum += await this.routeKm(list[i - 1], list[i])
        if (runId !== this._kmRun) return // 已被更新的计算取代
      }
      if (runId !== this._kmRun) return
      this.totalKm = Math.round(sum * 10) / 10
      this.kmLoading = false
    },

    // 详情弹窗：计算当前记忆与上一站/下一站的驾车路线距离
    async calcNeighborKm(m) {
      const list = this.ordered()
      const idx = list.findIndex(x => x.id === m.id)
      this.curKmLoading = true
      this.curPrevKm = null
      this.curNextKm = null
      if (idx > 0) this.curPrevKm = await this.routeKm(list[idx - 1], m)
      if (idx >= 0 && idx < list.length - 1) this.curNextKm = await this.routeKm(m, list[idx + 1])
      this.curKmLoading = false
    },

    // ---------------- 地图初始化（Leaflet） ----------------
    initMap() {
      // 瓦片本地缓存：打开 IndexedDB 并申请持久存储（异步，不阻塞地图渲染）
      TileCache.init()
      // 主坐标系 GCJ02：高德瓦片零误差，WGS84 源瓦片由 WgsTileLayer 纠偏
      this.map = L.map('earth-map', {
        crs: GCJCRS,
        scrollWheelZoom: true,
        doubleClickZoom: true,
        zoomControl: false,
        attributionControl: true
      })
      L.control.zoom({ position: 'bottomright' }).addTo(this.map)
      // 默认数据源：高德卫星
      this.applyMapSource()

      // 点击地图空白处 → 定位该点并打开添加对话框
      // 地图 CRS 为 GCJ02，e.latlng 即 GCJ02 坐标：先还原真实 WGS84，再转 BD09 存库（数据库保持 BD09）
      this.map.on('click', e => {
        // 点到标记/控件时不触发定位
        const t = e.originalEvent && e.originalEvent.target
        if (t && (t.classList.contains('leaflet-interactive')
          || t.classList.contains('leaflet-control')
          || t.classList.contains('leaflet-marker-icon'))) return
        const w = this.gcj2wgs(e.latlng.lng, e.latlng.lat)
        const bd = this.wgs2bd(w.lng, w.lat)
        this.geocodePoint(bd).then(address => {
          this.placeTempMarker(bd, address)
        })
      })

      // 中心坐标实时显示（经纬度定位栏）
      this.map.on('moveend', this.updateCenterCoord)

      // 支持分享链接定位：?lng=..&lat=..&zoom=..（命中参数时跳过默认视野）
      const urlLocated = this.applyUrlParams()

      // 先绘制已有记忆，再调整视野
      this.syncMarkers()
      this.syncRoute()
      if (!urlLocated) this.resetView()
    },

    // ---------------- earthol 形态：数据源 / 经纬度 / 全屏 / 分享 ----------------
    // 切换地图数据源（底图 + 可选标注叠加层）；WGS84 系瓦片源用 WgsTileLayer 纠偏到 GCJ02 网格
    applyMapSource() {
      if (!this.map) return
      this.tileLayers.forEach(l => this.map.removeLayer(l))
      this.tileLayers = []
      const src = MAP_SOURCES.find(s => s.key === this.mapSource)
      if (!src) return
      ;[...src.layers, ...(src.overlay || [])].forEach(k => {
        const t = TILE_SOURCES[k]
        const layer = t.wgs
          ? new CachedWgsTileLayer(t.url, { ...t.options, transform: (lng, lat) => this.gcj2wgs(lng, lat) })
          : new CachedTileLayer(t.url, t.options)
        this.tileLayers.push(layer.addTo(this.map))
        // 瓦片移出视野/切源时：标记已移除并回收 object URL
        layer.on('tileunload', this.onTileUnload)
      })
    },
    // 瓦片卸载处理：标记中止异步回填，回收缓存 object URL（防泄漏）
    onTileUnload(e) {
      const el = e.tile
      if (!el) return
      const img = el.tagName === 'IMG' ? el : (el.querySelector ? el.querySelector('img') : null)
      if (img) {
        img._tileAborted = true
        if (img._tileCacheUrl) TileCache.revoke(img._tileCacheUrl)
      }
    },

    changeMapSource() {
      // 覆盖物坐标统一为 GCJ02，切换瓦片源无需重建标记/路线
      this.applyMapSource()
      this.$message({ message: '已切换数据源', type: 'success' })
    },

    // 经纬度输入 → 定位（输入为真实 WGS84，转 GCJ02 显示）
    goToCoord() {
      const lng = parseFloat(this.lngInput)
      const lat = parseFloat(this.latInput)
      if (isNaN(lng) || isNaN(lat) || Math.abs(lng) > 180 || Math.abs(lat) > 90) {
        this.$message({ message: '请输入有效的经纬度（经度 -180~180，纬度 -90~90）', type: 'warning' })
        return
      }
      const g = this.wgs2gcj(lng, lat)
      this.map.setView([g.lat, g.lng], 13)
      this.$message({ message: '已定位到 ' + lng.toFixed(4) + ', ' + lat.toFixed(4), type: 'success' })
    },

    // 地图中心点坐标实时更新（显示真实 WGS84）
    updateCenterCoord() {
      if (!this.map) return
      const c = this.map.getCenter() // GCJ02
      const w = this.gcj2wgs(c.lng, c.lat)
      this.centerCoord = '中心 ' + w.lng.toFixed(4) + ', ' + w.lat.toFixed(4)
    },

    // 全屏切换（earthol 同款全屏按钮）
    toggleFullscreen() {
      const el = document.getElementById('earth-map')
      if (!el) return
      if (!document.fullscreenElement) {
        if (el.requestFullscreen) el.requestFullscreen()
      } else {
        if (document.exitFullscreen) document.exitFullscreen()
      }
    },

    // 分享当前地图位置：生成带真实 WGS84 经纬度/缩放参数的链接并复制
    shareLocation() {
      if (!this.map) return
      const c = this.map.getCenter() // GCJ02，转真实 WGS84 分享
      const w = this.gcj2wgs(c.lng, c.lat)
      const url = location.origin + location.pathname + '?lng=' + w.lng.toFixed(6) +
        '&lat=' + w.lat.toFixed(6) + '&zoom=' + this.map.getZoom()
      const done = () => this.$message({ message: '当前位置链接已复制', type: 'success' })
      const fail = () => this.$message({ message: '链接：' + url, type: 'info', duration: 6000 })
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done).catch(fail)
      } else {
        // 低版本浏览器降级：提示直接显示链接
        fail()
      }
    },

    // 打开页面时读取分享链接参数并定位；命中参数返回 true（调用方据此跳过默认视野）
    applyUrlParams() {
      if (!this.map) return false
      const p = new URLSearchParams(location.search)
      const lng = parseFloat(p.get('lng'))
      const lat = parseFloat(p.get('lat'))
      const zoom = parseInt(p.get('zoom'), 10)
      if (!isNaN(lng) && !isNaN(lat)) {
        // 链接为真实 WGS84，转 GCJ02 后定位
        const g = this.wgs2gcj(lng, lat)
        this.map.setView([g.lat, g.lng], isNaN(zoom) ? 13 : zoom)
        return true
      }
      return false
    },

    // 反地理编码：坐标（BD09）→ 地址文字（Photon 免 key 服务，接受 WGS84，先转换再请求）
    geocodePoint(point) {
      const w = this.bd2wgs(point.lng, point.lat)
      return fetch('https://photon.komoot.io/reverse?lon=' + w.lng + '&lat=' + w.lat)
        .then(r => r.json())
        .then(data => {
          const f = data && data.features && data.features[0]
          if (!f) return ''
          const p = f.properties || {}
          const parts = []
          if (p.name) parts.push(p.name)
          if (p.district && p.district !== p.name) parts.push(p.district)
          if (p.city && p.city !== p.district) parts.push(p.city)
          if (p.state && p.state !== p.city) parts.push(p.state)
          if (p.country && p.country !== p.state) parts.push(p.country)
          return parts.join('，')
        })
        .catch(() => '')
    },

    // ---------------- 标记 / 路线（Leaflet） ----------------
    // 地点标记：圆形缩略图 + 名称胶囊（divIcon）；点击查看详情，再点关闭
    createMarker(memory) {
      // 库内坐标为 BD09，按当前数据源基准（GCJ02/WGS84）转成显示坐标渲染
      const w = this.displayCoord(memory.lng, memory.lat)
      const photos = this.photoList(memory.photo)
      const first = photos.length ? photos[0] : ''
      const thumbHtml = this.isVideo(first)
        ? '<div class="album-marker-img album-marker-video" title="视频">▶</div>'
        : '<img class="album-marker-img" src="' + (first ? this.imgUrl(first) : this.defaultPhoto(memory)) + '" alt="" />'
      const marker = L.marker([w.lat, w.lng], {
        icon: L.divIcon({
          className: 'album-marker-icon',
          html: '<div class="album-marker">' +
            thumbHtml +
            '<span class="album-marker-name">' + this.escapeHtml(memory.name) + '</span>' +
            '</div>',
          iconSize: null, // 必须显式置空：DivIcon 默认 iconSize 为 [12,12]，显式 null 才不会生成内联宽高，宽度随文字自适应
          iconAnchor: [17, 17] // 锚点=图片圆心，与原圆点落点一致
        }),
        riseOnHover: true
      }).addTo(this.map)
      // 点击标记：详情正展示该地点 → 关闭（再次点击取消）；否则打开详情
      marker.on('click', e => {
        L.DomEvent.stopPropagation(e.originalEvent)
        if (this.current && this.current.id === memory.id && this.showDetail) {
          this.showDetail = false
          this.current = null
        } else {
          this.viewMemory(memory)
        }
      })
      this.markerMap[memory.id] = { marker }
    },

    removeMarker(id) {
      const item = this.markerMap[id]
      if (!item) return
      this.map.removeLayer(item.marker)
      delete this.markerMap[id]
    },

    // 全量重建标记（增删后调用）
    syncMarkers() {
      if (!this.map) return
      Object.keys(this.markerMap).forEach(id => this.removeMarker(id))
      this.memories.forEach(m => this.createMarker(m))
    },

    // 足迹路线：按用户顺序沿真实道路连接所有地点（虚线；无道路段降级为直线，异步绘制）
    async syncRoute() {
      if (!this.map) return
      if (this.routeLine) {
        this.map.removeLayer(this.routeLine)
        this.routeLine = null
      }
      if (!this.showRoute) return
      const runId = (this._routeRun = (this._routeRun || 0) + 1)
      const list = this.ordered()
      if (list.length < 2) return
      const points = [] // 显示基准（lat, lng）数组
      for (let i = 1; i < list.length; i++) {
        const r = await this.routePlan(list[i - 1], list[i])
        if (runId !== this._routeRun) return // 已被更新的绘制取代
        if (r.path && r.path.length > 1) {
          // 真实道路轨迹（BD09 → 显示基准）；与上一段衔接处去重
          const w0 = this.displayCoord(r.path[0].lng, r.path[0].lat)
          const last = points.length ? points[points.length - 1] : null
          const same = last && Math.abs(last[0] - w0.lat) < 1e-6 && Math.abs(last[1] - w0.lng) < 1e-6
          const arr = r.path.map(p => { const w = this.displayCoord(p.lng, p.lat); return [w.lat, w.lng] })
          points.push(...(same ? arr.slice(1) : arr))
        } else {
          // 无驾车路线：该段降级为直线
          const a = this.displayCoord(list[i - 1].lng, list[i - 1].lat)
          const b = this.displayCoord(list[i].lng, list[i].lat)
          points.push([a.lat, a.lng], [b.lat, b.lng])
        }
      }
      if (runId !== this._routeRun) return
      if (points.length < 2) return
      this.routeLine = L.polyline(points, {
        color: '#ff4d4f',
        weight: 6,
        opacity: 0.9,
        dashArray: '8 8'
      }).addTo(this.map)
    },

    // 两个点坐标是否相同（用于路线段衔接去重）
    samePoint(p1, p2) {
      return p1 && p2 && Math.abs(p1.lng - p2.lng) < 1e-6 && Math.abs(p1.lat - p2.lat) < 1e-6
    },

    toggleRoute() {
      this.showRoute = !this.showRoute
      this.syncRoute()
      this.$message({
        message: this.showRoute ? '已显示足迹路线' : '已隐藏足迹路线',
        type: 'success'
      })
    },

    // 视野：有记忆时框选所有地点，否则全国总览
    resetView() {
      if (!this.map) return
      if (!this.memories.length) {
        // 全国总览（原百度坐标 108,34 转当前显示基准）
        const c = this.displayCoord(108, 34)
        this.map.setView([c.lat, c.lng], 5)
        return
      }
      const lats = this.memories.map(m => m.lat)
      const lngs = this.memories.map(m => m.lng)
      const spanLng = Math.max(...lngs) - Math.min(...lngs)
      const spanLat = Math.max(...lats) - Math.min(...lats)
      const zoom = spanLng > 14 || spanLat > 10 ? 5 : spanLng > 6 ? 6 : spanLng > 2 ? 8 : 10
      const c = this.displayCoord((Math.min(...lngs) + Math.max(...lngs)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2)
      this.map.setView([c.lat, c.lng], zoom)
    },

    // ---------------- 搜索 ----------------
    // 按名称解析坐标（BD09），resolve 为 { point: 坐标, address: 逆地理地址 }；无结果 resolve(null)。
    // 使用 Photon（OpenStreetMap 免费地理编码，免 key）：按相关性返回最佳结果，
    // 坐标从 WGS84 转换回 BD09 存库；失败或无结果自动重试一次规避偶发超时。
    geocodeName(query) {
      const q = String(query || '').trim()
      if (!q) return Promise.resolve(null)
      const run = (text) => {
        return fetch('https://photon.komoot.io/api/?q=' + encodeURIComponent(text) + '&limit=5')
          .then(r => r.json())
          .then(data => {
            const fs = (data && data.features) || []
            if (!fs.length) return null
            // 取第一个有效结果：WGS84 → GCJ02 → BD09
            for (let i = 0; i < fs.length; i++) {
              const c = fs[i].geometry && fs[i].geometry.coordinates
              if (!c) continue
              const g = this.wgs2gcj(c[0], c[1])
              const b = this.gcj2bd(g.lng, g.lat)
              const p = fs[i].properties || {}
              const parts = []
              if (p.name) parts.push(p.name)
              if (p.district && p.district !== p.name) parts.push(p.district)
              if (p.city && p.city !== p.district) parts.push(p.city)
              if (p.state && p.state !== p.city) parts.push(p.state)
              if (p.country && p.country !== p.state) parts.push(p.country)
              return { point: { lng: b.lng, lat: b.lat }, address: parts.join('，') }
            }
            return null
          })
          .catch(() => null)
      }
      return run(q).then(res => {
        if (res) return res
        // 偶发失败/无结果：自动重试一次
        return run(q)
      })
    },

    // 搜索仅定位到地图：落下「标记 + 小标签牌」展示地点信息，右下角询问是否添加
    searchLocation() {
      if (!this.searchQuery.trim()) return
      const name = this.searchQuery.trim()
      // 关闭上一次搜索的残留通知，避免其 onClose 清理掉本次结果
      if (this.searchNotify) {
        this.searchNotify.close()
        this.searchNotify = null
      }
      if (!this._searchSeq) this._searchSeq = 0 // 未初始化时为 undefined，++undefined=NaN 会误拦截本次结果
      const seq = ++this._searchSeq
      this.geocodeName(name).then(res => {
        if (seq !== this._searchSeq) return // 已被更新的搜索取代，忽略本次结果
        if (res) {
          const point = res.point
          // 百度 Geocoder 返回 BD09，转当前显示基准后定位到 Leaflet
          const w = this.displayCoord(point.lng, point.lat)
          this.map.setView([w.lat, w.lng], 16) // 层级放大，避免搜索后还需手动放大才能看清地点
          this.searchPoint = point
          this.searchName = name
          this.placeSearchMarker(point, name, res.address)
          this.searchNotify = this.$notify({
            position: 'bottom-right', // 右下角弹出
            title: '📍 已定位',
            message: this.$createElement('div', { class: 'search-notify-body' }, [
              this.$createElement('p', { class: 'search-notify-text' }, '已定位到「' + name + '」，要添加到旅行相册吗？'),
              this.$createElement('el-button', {
                props: { type: 'primary', size: 'small', round: true },
                on: { click: () => this.confirmAddFromSearch() }
              }, '＋ 添加')
            ]),
            duration: 10000, // 10 秒后自动消失
            onClose: () => {
              // 通知消失只清理待添加坐标；小标签牌保留，直到点击取消或搜索新地点替换
              if (!this.showAdd) {
                this.searchPoint = null
                this.searchName = ''
              }
            }
          })
        } else {
          this.$message({
            message: '无法定位「' + name + '」，试试更完整的名称，如：黄石湖北师范大学',
            type: 'warning',
            duration: 5000
          })
        }
      })
    },

    // 点击通知里的「添加」：应用搜索坐标并打开添加弹窗
    confirmAddFromSearch() {
      if (!this.searchPoint) return
      this.pendingPoint = this.searchPoint
      this.form.name = this.searchName
      this.searchPoint = null
      this.searchName = ''
      this.clearSearchMarker()
      this.openAddDialog()
      if (this.searchNotify) {
        this.searchNotify.close()
        this.searchNotify = null
      }
    },

    // ---------------- 点击地图定位：右下角询问是否添加 ----------------
    // 放置临时定位标记并弹出右下角通知（复用搜索定位的通知样式）
    placeTempMarker(point, name) {
      this.clearTempMarker()
      const w = this.displayCoord(point.lng, point.lat)
      this.tempMarker = L.marker([w.lat, w.lng], {
        icon: L.divIcon({
          className: 'pending-pin-icon',
          html: '<div class="pending-pin">📍</div>',
          iconSize: [30, 36],
          iconAnchor: [15, 33]
        }),
        interactive: true // 拦截对标记本身的点击，避免再次触发地图定位
      }).addTo(this.map)
      this.clickPoint = point
      this.clickName = name || ''
      this.clickNotify = this.$notify({
        position: 'bottom-right',
        title: '📍 已定位',
        message: this.$createElement('div', { class: 'search-notify-body' }, [
          this.$createElement('p', { class: 'search-notify-text' }, '已定位到「' + (name || '该地点') + '」，要添加到旅行相册吗？'),
          this.$createElement('el-button', {
            props: { type: 'primary', size: 'small', round: true },
            on: { click: () => this.confirmAddFromClick() }
          }, '＋ 添加')
        ]),
        duration: 10000, // 10 秒后自动消失
        onClose: () => {
          // 未点「添加」时清理临时标记，避免误用点击定位的坐标
          if (!this.showAdd) this.clearTempMarker()
        }
      })
    },

    // 点击通知里的「添加」：应用点击定位的坐标并打开添加弹窗
    confirmAddFromClick() {
      if (!this.clickPoint) return
      this.pendingPoint = this.clickPoint
      this.form.name = this.clickName || ''
      this.clearTempMarker()
      this.openAddDialog()
      if (this.clickNotify) {
        this.clickNotify.close()
        this.clickNotify = null
      }
    },

    // 移除地图上的临时定位标记并清空待添加坐标
    clearTempMarker() {
      if (this.tempMarker) {
        this.map.removeLayer(this.tempMarker)
        this.tempMarker = null
      }
      this.clickPoint = null
      this.clickName = ''
    },

    // ---------------- 搜索定位标记（小标签牌，不放大头针以免遮挡位置） ----------------
    // 搜索成功后在地点插小标签牌（箭头尖端即精确位置）：优先展示相册同名地点的信息（图/标题/地址），
    // 没有相册信息时展示「名称 + 逆地理地址」；两者都没有则只显示名称。
    // 标签牌常驻不随通知消失：点击标签牌取消，搜索新地点时自动替换旧标签牌
    placeSearchMarker(point, name, address) {
      this.clearSearchMarker()
      const w = this.displayCoord(point.lng, point.lat)
      // 相册同名（或名称互相包含）的地点优先展示其信息
      const match = this.memories.find(m => m.name === name) ||
        this.memories.find(m => name.indexOf(m.name) >= 0 || (m.name && m.name.indexOf(name) >= 0)) || null
      let body = ''
      if (match) {
        const photos = this.photoList(match.photo)
        const first = photos.length ? photos[0] : ''
        const thumbHtml = this.isVideo(first)
          ? '<div class="search-label-img search-label-video" title="视频">▶</div>'
          : '<img class="search-label-img" src="' + (first ? this.imgUrl(first) : this.defaultPhoto(match)) + '" alt="" />'
        const addrText = match.address || address || ''
        body = thumbHtml +
          '<div class="search-label-info">' +
          '<div class="search-label-name">' + this.escapeHtml(match.name || name) + '</div>' +
          (match.title ? '<div class="search-label-title">' + this.escapeHtml(match.title) + '</div>' : '') +
          (addrText ? '<div class="search-label-addr">' + this.escapeHtml(addrText) + '</div>' : '') +
          '</div>'
      } else if (address) {
        body = '<div class="search-label-info">' +
          '<div class="search-label-name">' + this.escapeHtml(name) + '</div>' +
          '<div class="search-label-addr">' + this.escapeHtml(address) + '</div>' +
          '</div>'
      } else {
        // 无地址也无相册信息：标签牌只显示名称
        body = '<div class="search-label-name">' + this.escapeHtml(name) + '</div>'
      }
      this.searchMarker = L.marker([w.lat, w.lng], {
        icon: L.divIcon({
          className: 'search-label-icon',
          html: '<div class="search-label-wrap"><div class="search-label">' + body +
            '</div><div class="search-label-arrow"></div></div>',
          iconSize: null,
          iconAnchor: [0, 0] // 标签牌箭头尖端正对精确位置
        }),
        interactive: true // 拦截对标记本身的点击，避免再次触发地图定位
      }).addTo(this.map)
      // 点击小标签牌 → 取消该标记（下一次搜索会先清除旧标签牌再放新的）
      this.searchMarker.on('click', e => {
        L.DomEvent.stopPropagation(e.originalEvent)
        this.clearSearchMarker()
      })
    },

    // 移除搜索定位标记
    clearSearchMarker() {
      if (this.searchMarker) {
        this.map.removeLayer(this.searchMarker)
        this.searchMarker = null
      }
    },

    // ---------------- 添加 / 删除 ----------------
    openAddDialog() {
      if (!this.form.date) this.form.date = this.today()
      this.editingId = null // 强制进入「添加」模式，防止编辑状态残留
      // 每次打开都重新计算「下一个编号」（当前最大编号 + 1，可手动改成任意位置），
      // 避免弹窗关闭再打开时输入框残留旧值导致默认编号不变
      const maxNo = this.memories.reduce((max, m) => Math.max(max, m.sortNo || 0), 0)
      this.form.sortNo = maxNo + 1
      this.showAdd = true
    },

    // 编辑：预填当前记忆内容，保留原坐标，打开添加弹窗（编辑模式）
    openEdit() {
      if (!this.current) return
      const c = this.current
      this.editingId = c.id
      this.form = {
        name: c.name || '',
        title: c.title || '',
        date: c.date || this.today(),
        description: c.description || '',
        photo: c.photo || '',
        sortNo: c.sortNo || null
      }
      // 默认沿用原坐标，用户可重新搜索/点击地图更换位置
      this.pendingPoint = { lat: c.lat, lng: c.lng }
      this.showDetail = false
      this.current = null
      this.showAdd = true
    },

    // 取消/关闭添加、编辑弹窗：退出编辑模式并清空表单与待定坐标
    // （任何关闭方式都会触发：取消按钮、右上角 X、ESC）
    closeAdd() {
      this.showAdd = false
      this.editingId = null
      this.form = { name: '', title: '', date: '', description: '', photo: '', sortNo: null }
      this.pendingPoint = null
    },

    today() {
      const d = new Date()
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },

    // 上传照片/视频（支持一次多选多张）：图片逐张压缩上传，视频原文件直传，成功后以「|」拼接存入 form.photo
    onFilesChange(e) {
      const files = Array.prototype.slice.call(e.target.files || [])
      if (!files.length) return
      if (files.some(f => !f.type.startsWith('image/') && !f.type.startsWith('video/'))) {
        this.$message({ message: '请选择图片或视频文件', type: 'warning' })
        e.target.value = ''
        return
      }
      e.target.value = '' // 允许重复选择同一文件
      let done = 0
      const finish = () => {
        done++
        if (done === files.length) this.$message({ message: '照片/视频上传完成', type: 'success' })
      }
      const uploadOne = fd => {
        this.$axios.post(this.$httpUrl + '/mapAlbum/api/uploadMapImg', fd)
          .then(res => {
            if (res.data && res.data.success) {
              this.form.photo = this.form.photo ? this.form.photo + '|' + res.data.filePath : res.data.filePath
            } else {
              this.$message({ message: (res.data && res.data.message) || '照片/视频上传失败', type: 'error' })
            }
          })
          .catch(() => {
            this.$message({ message: '照片/视频上传失败，请重试', type: 'error' })
          })
          .finally(finish)
      }
      files.forEach(file => {
        if (file.type.startsWith('video/')) {
          // 视频：不压缩，原文件直传（保留清晰度与时长）
          const m = /\.([a-zA-Z0-9]+)$/.exec(file.name || '')
          const ext = m ? m[1].toLowerCase() : 'mp4'
          const fd = new FormData()
          fd.append('file', file, 'map_video.' + ext)
          uploadOne(fd)
        } else {
          this.compressImage(file).then(blob => {
            // 按实际格式取名，保证后端落盘扩展名正确（GIF 直传保留动画与透明）
            const ext = file.type === 'image/gif' ? 'gif'
              : (file.type === 'image/png' || file.type === 'image/webp') ? 'png' : 'jpg'
            const fd = new FormData()
            fd.append('file', blob, 'map_photo.' + ext)
            uploadOne(fd)
          }).catch(() => {
            this.$message({ message: '图片解析失败', type: 'error' })
            finish()
          })
        }
      })
    },

    // photo 路径串（「|」分隔）→ 数组
    photoList(p) {
      if (!p) return []
      return String(p).split('|').map(s => s.trim()).filter(Boolean)
    },

    // 记忆的第一张照片路径（列表缩略图 / 地图标记用）
    firstPhoto(memory) {
      const list = this.photoList(memory && memory.photo)
      return list.length ? list[0] : ''
    },

    // 判断路径是否为视频（map-video 目录或常见视频后缀）
    isVideo(path) {
      if (!path) return false
      return /\/map-video\//.test(path) || /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(path)
    },

    // 移除表单中的第 i 张照片
    removeFormPhoto(i) {
      const list = this.photoList(this.form.photo)
      list.splice(i, 1)
      this.form.photo = list.join('|')
    },

    // HTML 转义（标记内渲染用户输入的地点名，防止 XSS）
    escapeHtml(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, c => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ))
    },

    // 详情弹窗：切换当前地点的上一张/下一张图片
    prevPhoto() {
      if (this.curPhotoIdx > 0) this.curPhotoIdx--
    },
    nextPhoto() {
      if (this.curPhotoIdx < this.curPhotoCount - 1) this.curPhotoIdx++
    },

    // 图片处理：GIF 原文件直传（保留动画与透明）；PNG/WebP 导出 PNG 保留透明；其余铺白底转 JPEG 防止黑底
    // 1280px / 0.85：详情大图区域约 720px，2 倍分辨率下仍清晰
    compressImage(file, maxWidth = 1280, quality = 0.85) {
      return new Promise((resolve, reject) => {
        // GIF 动图：不做压缩，直接上传原文件，保留动画效果与透明背景
        if (file.type === 'image/gif') {
          resolve(file)
          return
        }
        const reader = new FileReader()
        reader.onload = ev => {
          const img = new Image()
          img.onload = () => {
            const scale = Math.min(1, maxWidth / img.width)
            const w = Math.round(img.width * scale)
            const h = Math.round(img.height * scale)
            const canvas = document.createElement('canvas')
            canvas.width = w
            canvas.height = h
            const ctx = canvas.getContext('2d')
            // 可能带透明的格式（PNG/WebP）导出 PNG 保留透明；其余格式先铺白底，避免透明区域转成黑色
            const keepAlpha = file.type === 'image/png' || file.type === 'image/webp'
            if (!keepAlpha) {
              ctx.fillStyle = '#ffffff'
              ctx.fillRect(0, 0, w, h)
            }
            ctx.drawImage(img, 0, 0, w, h)
            canvas.toBlob(blob => {
              if (blob) resolve(blob)
              else reject(new Error('图片压缩失败'))
            }, keepAlpha ? 'image/png' : 'image/jpeg', quality)
          }
          img.onerror = () => reject(new Error('图片解析失败'))
          img.src = ev.target.result
        }
        reader.onerror = () => reject(new Error('文件读取失败'))
        reader.readAsDataURL(file)
      })
    },

    saveMemory() {
      if (!this.form.name || !this.form.title) {
        this.$message({ message: '请填写地点和标题', type: 'warning' })
        return
      }
      if (!this.user || !this.user.no) {
        this.$message({ message: '请先登录后再保存旅行记忆', type: 'warning' })
        return
      }
      // 未点地图 / 未搜索定位：先按地点名称智能定位，定位成功再保存
      if (!this.pendingPoint) {
        this.geocodeName(this.form.name).then(res => {
          if (res) {
            this.pendingPoint = res.point
            this.doSave()
          } else {
            this.$message({ message: '无法定位「' + this.form.name + '」，请点击地图选择位置', type: 'warning' })
          }
        })
        return
      }
      this.doSave()
    },

    // 实际执行保存（此时已有坐标；editingId 存在时为修改）
    doSave() {
      const isEdit = !!this.editingId
      const payload = {
        userId: this.user.id,
        userNo: this.user.no,
        userName: this.user.name || '',
        name: this.form.name,
        title: this.form.title,
        date: this.form.date || this.today(),
        description: this.form.description,
        photo: this.form.photo,
        lat: this.pendingPoint.lat,
        lng: this.pendingPoint.lng,
        // 编号：有输入才传（新增留空=自动补；编辑留空=保持原编号）
        ...(this.form.sortNo ? { sortNo: Number(this.form.sortNo) } : {}),
        ...(isEdit ? { id: this.editingId } : {})
      }
      this.$axios.post(this.$httpUrl + (isEdit ? '/mapAlbum/update' : '/mapAlbum/save'), payload)
        .then(res => {
          if (res.data && res.data.code == 200) {
            const memory = res.data.data
            if (isEdit) {
              // 修改：替换列表项并重建标记（坐标可能已变化）
              const idx = this.memories.findIndex(m => m.id === memory.id)
              if (idx > -1) this.memories.splice(idx, 1, memory)
              this.syncMarkers()
            } else {
              this.memories.push(memory)
              this.createMarker(memory)
            }
            this.syncRoute()
            this.calcTotalKm()
            // 定位到保存地点并清空表单/退出编辑
            if (this.map) {
              const w = this.displayCoord(memory.lng, memory.lat)
              this.map.setView([w.lat, w.lng], 13)
            }
            this.showAdd = false
            this.showDetail = false
            this.current = null
            this.editingId = null
            this.form = { name: '', title: '', date: this.today(), description: '', photo: '', sortNo: null }
            this.pendingPoint = null
            this.$message({ message: isEdit ? '旅行记忆已更新 ✨' : '旅行记忆已保存 🎉', type: 'success' })
          } else {
            this.$message({ message: (res.data && res.data.msg) || '保存失败', type: 'error' })
          }
        })
        .catch(() => {
          this.$message({ message: '保存失败，请检查网络', type: 'error' })
        })
    },

    viewMemory(memory) {
      this.current = memory
      this.curPhotoIdx = 0 // 切换地点后回到该地点的第一张图
      this.showDetail = true
      this.calcNeighborKm(memory)
    },

    // 详情弹窗：左右切换上/下一站（键盘 ← → 同样支持）
    prevMemory() {
      const idx = this.currentIndex
      if (idx > 0) this.viewMemory(this.memories[idx - 1])
    },
    nextMemory() {
      const idx = this.currentIndex
      if (idx > -1 && idx < this.memories.length - 1) this.viewMemory(this.memories[idx + 1])
    },
    handleDetailKey(e) {
      if (!this.showDetail) return
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        // 该地点有多张图时优先切图，单图时切上一站
        if (this.curPhotoCount > 1) this.prevPhoto()
        else this.prevMemory()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        if (this.curPhotoCount > 1) this.nextPhoto()
        else this.nextMemory()
      }
    },

    // 上移/下移调整地点顺序（服务端交换 sortNo，成功后重载列表与路线）
    moveMemory(direction) {
      const memory = this.current
      if (!memory) return
      if (!this.user || !this.user.no) {
        this.$message({ message: '请先登录', type: 'warning' })
        return
      }
      this.$axios.post(this.$httpUrl + '/mapAlbum/move', {
        id: memory.id,
        userNo: this.user.no,
        direction
      })
        .then(res => {
          if (res.data && res.data.code == 200) {
            this.loadMemories(true) // 保留当前地图视野，仅刷新列表与路线
            this.$message({ message: direction === 'up' ? '已上移 ↑' : '已下移 ↓', type: 'success' })
          } else {
            this.$message({ message: (res.data && res.data.msg) || '调整失败', type: 'warning' })
          }
        })
        .catch(() => {
          this.$message({ message: '调整失败，请检查网络', type: 'error' })
        })
    },

    goToCurrent() {
      if (!this.current) return
      const w = this.displayCoord(this.current.lng, this.current.lat)
      this.map.setView([w.lat, w.lng], 13)
      this.showDetail = false
    },

    deleteMemory() {
      const memory = this.current
      if (!memory) return
      if (!this.user || !this.user.no) {
        this.$message({ message: '请先登录', type: 'warning' })
        return
      }
      this.$confirm('确定删除「' + memory.title + '」这条旅行记忆吗？', '提示', {
        type: 'warning',
        confirmButtonText: '删除',
        cancelButtonText: '取消'
      })
        .then(() => {
          this.$axios.post(this.$httpUrl + '/mapAlbum/delete', {
            id: memory.id,
            userNo: this.user.no
          })
            .then(res => {
              if (res.data && res.data.code == 200) {
                const idx = this.memories.findIndex(m => m.id === memory.id)
                if (idx > -1) this.memories.splice(idx, 1)
                this.removeMarker(memory.id)
                this.syncRoute()
                this.calcTotalKm()
                this.showDetail = false
                this.current = null
                this.$message({ message: '已删除', type: 'success' })
              } else {
                this.$message({ message: (res.data && res.data.msg) || '删除失败', type: 'error' })
              }
            })
            .catch(() => {
              this.$message({ message: '删除失败，请检查网络', type: 'error' })
            })
        })
        .catch(() => {})
    },

    clearAll() {
      if (!this.memories.length) return
      if (!this.user || !this.user.no) {
        this.$message({ message: '请先登录', type: 'warning' })
        return
      }
      this.$confirm('确定清空整个旅行相册吗？该操作不可恢复。', '提示', {
        type: 'warning',
        confirmButtonText: '清空',
        cancelButtonText: '取消'
      })
        .then(() => {
          this.$axios.post(this.$httpUrl + '/mapAlbum/clear', {
            userNo: this.user.no
          })
            .then(res => {
              if (res.data && res.data.code == 200) {
                this.memories = []
                this.totalKm = 0
                this.syncMarkers()
                this.syncRoute()
                this.resetView()
                this.$message({ message: '相册已清空', type: 'success' })
              } else {
                this.$message({ message: (res.data && res.data.msg) || '清空失败', type: 'error' })
              }
            })
            .catch(() => {
              this.$message({ message: '清空失败，请检查网络', type: 'error' })
            })
        })
        .catch(() => {})
    },

    // ---------------- 登录状态 ----------------
    // 从 sessionStorage 读取当前登录用户
    readLoginUser() {
      try {
        const cu = sessionStorage.getItem('CurUser')
        this.user = cu ? JSON.parse(cu) : null
      } catch (e) {
        this.user = null
      }
    },

    // 登录/登出广播处理：登录后自动加载该账号记忆，登出后清空本地视图
    onUserChanged() {
      this.readLoginUser()
      if (this.user && this.user.no) {
        this.loadMemories()
      } else {
        this.memories = []
        this.syncMarkers()
        this.syncRoute()
      }
    },

    // ---------------- 后端存取 ----------------
    // 从后端加载当前账号的旅行记忆（keepView=true 时不重置地图视野）
    loadMemories(keepView) {
      if (!this.user || !this.user.no) {
        this.memories = []
        return
      }
      this.$axios.get(this.$httpUrl + '/mapAlbum/list', {
        params: { userNo: this.user.no }
      })
        .then(res => {
          if (res.data && res.data.code == 200) {
            this.memories = res.data.data || []
            this.syncMarkers()
            this.syncRoute()
            this.calcTotalKm()
            if (!keepView) this.resetView()
          } else {
            this.$message({ message: (res.data && res.data.msg) || '加载失败', type: 'error' })
          }
        })
        .catch(() => {
          this.$message({ message: '加载旅行相册失败，请检查网络', type: 'error' })
        })
    },

    // 照片/资源地址：相对路径拼接后端地址
    imgUrl(path) {
      if (!path) return ''
      if (/^https?:\/\//.test(path)) return path
      return this.$httpUrl + path
    },

    // 默认配图（按 id 轮换）
    defaultPhoto(memory) {
      let hash = 0
      const s = String(memory.id)
      for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0
      return DEFAULT_PHOTOS[hash % DEFAULT_PHOTOS.length]
    }
  }
}
</script>

<style scoped>
/* 整体布局：地图铺满，面板浮在上层 */
.travel-album {
  position: relative;
  width: 100%;
  height: calc(100vh - 60px);
  margin-top: 60px;
  overflow: hidden;
  background: #fff5f9;
}

.map {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* Leaflet 内部 pane z-index 较高，容器建独立层叠上下文避免盖住悬浮面板 */
  z-index: 0;
  background: #fdf2f7;
}

/* ---------- 左侧相册面板 ---------- */
.album-panel {
  position: absolute;
  top: 16px;
  left: 16px;
  width: 300px;
  max-height: calc(100vh - 92px);
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.93);
  border-radius: 18px;
  box-shadow: 0 6px 24px rgba(217, 93, 139, 0.18);
  z-index: 10;
  overflow: hidden;
}

.panel-header {
  padding: 16px 18px 10px;
  background: linear-gradient(135deg, #ffe3ee, #ffd0e2);
}

.panel-header h2 {
  margin: 0;
  font-size: 18px;
  color: #c2457c;
  letter-spacing: 1px;
}

.panel-header .stats {
  margin: 6px 0 0;
  font-size: 12px;
  color: #b36a8c;
}

.panel-header .stats b {
  color: #e05595;
  font-size: 15px;
}

.memory-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px 8px 10px 10px;
  min-height: 100px;
  /* 兼容 Chrome/Firefox：scrollbar-width 控制悬浮式滚动条粗细（::-webkit-scrollbar 在部分新版浏览器不生效） */
  scrollbar-width: thin;
  scrollbar-color: #fcb4d0 transparent;
}

/* 记忆卡片 */
.memory-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  margin-bottom: 8px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #ffe0ec;
  cursor: pointer;
  transition: all 0.2s;
}

.memory-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(224, 85, 149, 0.22);
  border-color: #ffb7d4;
}

.thumb {
  width: 54px;
  height: 54px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
  background: #ffe3ee;
}
/* 视频缩略图：显示首帧 + 右下角播放角标 */
.thumb-video {
  position: relative;
  overflow: hidden;
  background: #1b0d16;
}
.thumb-video video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.thumb-video::after {
  content: '▶';
  position: absolute;
  right: 5px;
  bottom: 4px;
  font-size: 10px;
  line-height: 1;
  color: #fff;
  text-shadow: 0 0 3px rgba(0, 0, 0, 0.8);
}

.card-info {
  min-width: 0;
}

.card-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #7a3357;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.field-tip {
  margin-top: 4px;
  font-size: 11px;
  color: #b98ba2;
}

.card-no {
  display: inline-block;
  min-width: 20px;
  height: 20px;
  line-height: 20px;
  text-align: center;
  border-radius: 10px;
  background: #f8cddf;
  color: #a83c74;
  font-size: 11.5px;
  font-weight: 700;
  margin-right: 6px;
  vertical-align: 1px;
}

.card-date {
  margin-top: 4px;
  font-size: 11.5px;
  color: #b98ba2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.empty {
  padding: 30px 0;
  text-align: center;
  font-size: 13px;
  line-height: 1.8;
  color: #c99ab2;
}

.panel-footer {
  padding: 10px 14px;
  border-top: 1px solid #ffe6f0;
  text-align: center;
}

/* ---------- 右上工具条 ---------- */
.top-bar {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  max-width: calc(100% - 340px);
  z-index: 10;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.93);
  border-radius: 20px;
  padding: 4px 4px 4px 12px;
  box-shadow: 0 4px 14px rgba(217, 93, 139, 0.16);
}

.search-input {
  width: 140px;
  border: none;
  outline: none;
  font-size: 13px;
  color: #7a3357;
  background: transparent;
}

/* ---------- earthol 形态：经纬度定位栏 ---------- */
.coord-bar {
  position: absolute;
  top: 64px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.93);
  border-radius: 20px;
  padding: 5px 12px;
  box-shadow: 0 4px 14px rgba(217, 93, 139, 0.16);
}

.coord-label {
  font-size: 12px;
  font-weight: 600;
  color: #c2457c;
  margin-right: 2px;
  white-space: nowrap;
}

.coord-input {
  width: 84px;
}

.coord-input .el-input__inner {
  border-radius: 10px;
}

.coord-current {
  font-size: 11.5px;
  color: #b36a8c;
  margin-left: 2px;
  white-space: nowrap;
}

/* ---------- earthol 形态：地图数据源切换器 ---------- */
.source-switch {
  position: absolute;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);

  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.94);
  border-radius: 15px;
  padding: 5px 12px;
  box-shadow: 0 4px 14px rgba(217, 93, 139, 0.16);
}

.source-label {
  font-size: 12px;
  font-weight: 600;
  color: #c2457c;
  white-space: nowrap;
}

/* 数据源切换器：选中态粉色主题（与站点风格一致） */
::v-deep .source-switch .el-radio-button__inner {
  border-radius: 8px !important;
  border-color: #ffd9e8 !important;
  background: #fff !important;
  color: #b36a8c !important;
  font-size: 12px !important;
}
::v-deep .source-switch .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: linear-gradient(135deg, #ff9ec3, #d9598a) !important;
  border-color: #d9598a !important;
  color: #fff !important;
  box-shadow: -1px 0 0 0 #d9598a !important;
}
::v-deep .source-switch .el-radio-button:first-child .el-radio-button__inner {
  border-radius: 10px 0 0 10px !important;
}
::v-deep .source-switch .el-radio-button:last-child .el-radio-button__inner {
  border-radius: 0 10px 10px 0 !important;
}

/* 底部提示 */
.hint {
  position: absolute;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  padding: 6px 18px;
  border-radius: 20px;
  font-size: 12.5px;
  color: #c2457c;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 4px 14px rgba(217, 93, 139, 0.16);
}

/* ---------- 表单与详情 ---------- */
.field {
  margin-bottom: 14px;
}

.field label {
  display: block;
  margin-bottom: 6px;
  font-size: 12.5px;
  color: #b36a8c;
}

.photo-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.photo-preview {
  width: 64px;
  height: 64px;
  border-radius: 10px;
  object-fit: cover;
  border: 1px solid #ffd9e8;
}

/* 添加/编辑弹窗：20px 圆角（el-dialog 没有 borderRadius 属性，需通过 CSS 设置） */
::v-deep .album-dialog {
  border-radius: 20px;
  overflow: hidden;
}

/* 详情弹窗：高级质感布局（图片居中放大 + 底部信息栏 + 左右切换，固定高度不产生滚动条） */
/* 16:10 半透明玻璃弹窗 */
::v-deep .detail-dialog {
  /* 宽度与上边距由模板 width/top 属性控制，CSS 不再覆盖；高度由内容决定（无固定比例） */
  border-radius: 20px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #ffffff21;
  backdrop-filter: blur(9px) saturate(140%);
  -webkit-backdrop-filter: blur(9px) saturate(140%);
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 24px 64px rgba(30, 8, 20, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.8);
}
/* 头部压缩为 0，关闭按钮改放到弹窗主体右上角（图片区右上） */
::v-deep .detail-dialog .el-dialog__header {
  height: 0;
  min-height: 0;
  padding: 0;
  overflow: visible;
  background: transparent;
  border: none;
}
::v-deep .detail-dialog .el-dialog__headerbtn {
  top: 12px;
  right: 14px;
  width: 36px;
  height: 36px;
  z-index: 30;
  line-height: 34px;
}
::v-deep .detail-dialog .el-dialog__headerbtn .el-dialog__close {
  color: #b35a7e;
  font-size: 18px;
}
::v-deep .detail-dialog .el-dialog__headerbtn:hover .el-dialog__close {
  color: #e0539c;
}
::v-deep .detail-dialog .el-dialog__headerbtn:active .el-dialog__close {
  color: #a03d63;
}
::v-deep .detail-dialog .el-dialog__body {
  flex: 1;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  display: flex;
  border-radius: 20px !important;
  margin: 10px 10px 10px !important;
  background: #ffffff40 !important;

}
.detail-stage {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.34) 0%, rgba(255, 240, 248, 0.26) 100%);
}
/* 中间大图区 */
.detail-stage-body {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
/* 图片固定显示尺寸：不随原图大小变化，弹窗布局保持稳定 */
.detail-photo-big {
  //width: min(72%, 720px);
  max-width: 100%;
  height: min(65vh, 685px);
  object-fit: cover;
  display: block;
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.12);
}
.detail-photo-big.video-big {
  object-fit: contain;
  background: #1b0d16;
}
/* 左右切换箭头（毛玻璃） */
.nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 45px;
  height: 45px;
  border: 1px solid rgba(120, 60, 80, 0.35);
  border-radius: 50%;
  font-size: 34px;
  line-height: 1;
  color: #fff;
  background: rgba(96, 44, 66, 0.45);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 8px 24px rgba(96, 44, 66, 0.28);
  cursor: pointer;
  transition: all 0.25s;
  z-index: 2;
}
.nav-prev { left: 22px; }
.nav-next { right: 22px; }
.nav-arrow:hover:not(:disabled) {
  background: rgba(255, 158, 195, 0.4);
  border-color: rgba(255, 158, 195, 0.7);
  transform: translateY(-50%) scale(1.08);
}
.nav-arrow:disabled {
  opacity: 0.28;
  cursor: not-allowed;
}
/* 右下角：第几张 / 共几张 */
.detail-count {
  position: absolute;
  right: 20px;
  bottom: 16px;
  padding: 5px 16px;
  border-radius: 999px;
  background: rgba(96, 44, 66, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: #fff;
  font-size: 13px;
  letter-spacing: 1.5px;
}
/* 底部信息栏（悬浮圆角卡片） */
.detail-info {
  flex-shrink: 0;
  margin: 0 12px 12px;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 18px;
  padding: 12px 24px 14px;
  box-shadow: 0 -8px 32px rgba(217, 93, 139, 0.12);
  display: flex;
  flex-direction: column;
  max-height: 240px;
}
.detail-info h3 {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: linear-gradient(90deg, #c2276f, #ff6fa5);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.detail-meta {
  align-self: flex-start;
  margin: 0 0 8px;
  padding: 3px 14px;
  border-radius: 999px;
  background: rgba(255, 240, 246, 0.85);
  border: 1px solid rgba(255, 158, 195, 0.4);
  color: #a85078;
  font-size: 12.5px;
}
.detail-desc {
  margin: 0 0 10px;
  font-size: 13px;
  line-height: 1.7;
  color: #5d3348;
  white-space: pre-wrap;
  max-height: 84px;
  overflow-y: auto;
  background: rgba(255, 233, 243, 0.74);
  border: 1px solid rgba(246, 142, 142, 0.27);
  border-radius: 12px;
  padding: 10px 14px;
}
.detail-desc::-webkit-scrollbar {
  width: 5px;
}
.detail-desc::-webkit-scrollbar-thumb {
  background: #ffc2d8;
  border-radius: 4px;
}
.detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}
.detail-actions .el-button {
  border-radius: 10px;
  font-weight: 500;
}
/* 小屏适配 */
@media (max-width: 720px) {
  ::v-deep .detail-dialog {
    width: 96vw !important;
    height: 92vh !important;
    margin: 4vh auto !important;
  }
  .nav-arrow { width: 44px; height: 44px; font-size: 27px; }
  .nav-prev { left: 10px; }
  .nav-next { right: 10px; }
  .detail-info { margin: 0 8px 8px; padding: 12px 16px 14px; max-height: 220px; }
  .detail-photo-big { height: min(44vh, 460px); }
}

/* 详情弹窗：与上一站/下一站距离 */
.detail-dist {
  margin: 4px 0 8px;
  font-size: 13px;
  color: #e0539c;
}

/* 地图右上角：足迹总里程角标 */
.mileage-badge {
  position: absolute;
  top: 122px;
  right: 16px;
  padding: 10px 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.93);
  box-shadow: 0 6px 24px rgba(217, 93, 139, 0.18);
  text-align: right;
  z-index: 10;
}

.mileage-num {
  font-size: 20px;
  font-weight: 700;
  color: #e0539c;
}

.mileage-num span {
  font-size: 12px;
  font-weight: 400;
  color: #b07a97;
}

.mileage-sub {
  font-size: 12px;
  color: #b07a97;
}

/* 相册列表滚动条美化（改细：6px -> 4px） */
.memory-list::-webkit-scrollbar {
  width: 4px;
}

.memory-list::-webkit-scrollbar-thumb {
  background: #ffc9de;
  border-radius: 2px;
}

.memory-list::-webkit-scrollbar-track {
  background: transparent;
}

/* 多图上传缩略图 */
.photo-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
  min-width: 0;
}
.photo-thumb-wrap {
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
}
.photo-thumb {
  display: block;
  width: 64px;
  height: 64px;
  border-radius: 10px;
  object-fit: cover;
  border: 1px solid #ffd9e8;
}
.photo-thumb.video-thumb {
  background: #1b0d16;
}
.photo-thumb-del {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  line-height: 16px;
  text-align: center;
  border-radius: 50%;
  background: #e0539c;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}
.photo-thumb-del:hover {
  background: #c2276f;
}

/* 详情弹窗：底部操作行两端的「上一地点/下一地点」箭头 */
.detail-actions .loc-arrow {
  border-color: #ffc2d6 !important;
  color: #d95d8b !important;
  background: #fff !important;
}
.detail-actions .loc-arrow:hover:not(:disabled) {
  border-color: #d9598a !important;
  color: #fff !important;
  background: linear-gradient(135deg, #ff9ec3, #d9598a) !important;
}
.detail-actions .loc-arrow:disabled {
  opacity: 0.45;
}
</style>
<style>
/* 全局：浅化 Element 弹窗遮罩，保证半透明玻璃能看到背后的地图 */
.v-modal {
  background: rgba(0, 0, 0, 0.25) !important;
}

/* Leaflet：粉色胶囊标签（标记上的常显地点名，等价原百度 Label 观感） */
.album-tooltip {
  background: rgba(255, 255, 255, 0.94) !important;
  border: 1px solid #ffc2d6 !important;
  border-radius: 10px !important;
  color: #d95d8b !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  padding: 2px 8px !important;
  box-shadow: 0 1px 4px rgba(217, 93, 139, 0.25) !important;
  white-space: nowrap;
}

/* 去掉 Leaflet tooltip 默认的小三角箭头 */
.album-tooltip::before {
  display: none !important;
}

/* Leaflet：地点标记（圆形缩略图 + 名称胶囊，divIcon 渲染在标记层） */
.album-marker-icon {
  background: transparent;
  border: none;
}
.album-marker {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 2px 10px 2px 2px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.8);
  border: 1.5px solid #ffb7d4;
  box-shadow: 0 3px 10px rgba(194, 39, 111, 0.5);
}
.album-marker-img {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid #ffffff;
  background: #ffe3ee;
}
.album-marker-img.album-marker-video,
.search-label-img.search-label-video {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  color: #fff;
  background: #d96fc2;
  border-color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
}
.album-marker-name {
  max-width: 120px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: #c2276f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 点击地图定位的临时标记（📍 大头针） */
.pending-pin-icon {
  background: transparent;
  border: none;
}
.pending-pin {
  width: 30px;
  height: 30px;
  font-size: 20px;
  line-height: 30px;
  text-align: center;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  box-shadow: 0 2px 10px rgba(224, 83, 156, 0.5);
}

/* 搜索定位的标记：小标签牌 + 箭头（宽度自适应、水平居中于标记点，箭头尖端即位置） */
.search-label-icon {
  background: transparent;
  border: none;
}
.search-label-wrap {
  transform: translate(-50%, -100%);
}
.search-label {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 280px;
  padding: 6px 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.97);
  border: 1.5px solid #ffb7d4;
  box-shadow: 0 4px 14px rgba(194, 39, 111, 0.35);
}
.search-label-img {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  object-fit: cover;
  border: 1.5px solid #ffffff;
  background: #ffe3ee;
  flex: none;
}
.search-label-info {
  min-width: 0;
}
.search-label-name {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  color: #c2276f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.search-label-title {
  font-size: 12px;
  line-height: 1.3;
  color: #666666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.search-label-addr {
  font-size: 11px;
  line-height: 1.3;
  color: #999999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.search-label-arrow {
  width: 0;
  height: 0;
  margin: 0 auto;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 7px solid #ffb7d4;
}
</style>
